import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import type { RouteLocationNamedRaw } from 'vue-router';
import { useUserStore } from '@/stores/userStore';
import { useAppConfig } from './useAppConfig';
import { useAuth } from './useAuth';
import { redirectUnauthenticatedUser } from '@/utils/auth';
import { useLDFlag } from 'launchdarkly-vue-client-sdk';
import { FeatureFlags } from '@/types/featureFlags';
import { usePermissions } from '@/composables/usePermissions';
import { toggleMaven } from '@/plugins/maven';

export class PrimaryNavigationItem {
  id?: string;
  title: string;
  appendIcon?: string;
  prependIcon?: string;
  enabled?: boolean;
  visible?: boolean;
  badge?: string;
  to?: RouteLocationNamedRaw;
  link?: string;
  action?(): void;

  constructor(navItem: PrimaryNavigationItem) {
    this.id = navItem.id ?? crypto.randomUUID();
    this.title = navItem.title;
    this.appendIcon = navItem.appendIcon;
    this.prependIcon = navItem.prependIcon;
    this.enabled = navItem.enabled ?? true;
    this.visible = navItem.visible ?? true;
    this.badge = navItem.badge;
    this.to = navItem.to;
    this.link = navItem.link;
    this.action = navItem.action;
  }
}

const withDefaults = (items: PrimaryNavigationItem[]) => {
  return items.map((i) => new PrimaryNavigationItem(i));
};

export function usePrimaryNavigation() {
  const { t } = useI18n();
  const userStore = useUserStore();

  const isMenuItemShown = ({ visible }: PrimaryNavigationItem): boolean => !!visible;

  const { isTeamAdmin } = usePermissions();

  const isEarningsBenchmarksEnabled = useLDFlag(FeatureFlags.EARNINGS_BENCHMARKS, false);
  const isAgentsEnabled = useLDFlag(FeatureFlags.AGENTS_ENABLED, false);

  const mainItems = computed(() => withDefaults([
    {
      title: t('navigationMenu.budgets'),
      prependIcon: 'local_atm',
      to: { name: 'TeamBudgets' },
    },
    {
      title: t('navigationMenu.agreements'),
      prependIcon: 'text_snippet',
      to: { name: 'Agreements' },
      visible: isTeamAdmin.value,
    },
    {
      title: t('navigationMenu.payments'),
      prependIcon: 'currency_exchange',
      to: {
        name: 'Payments',
        query: { paymentStatuses: '0,40,50,60,100,110' },
      },
      visible: isTeamAdmin.value,
    },
    {
      title: t('navigationMenu.earningsBenchmarks'),
      prependIcon: 'pie_chart_outline',
      to: { name: 'EarningsBenchmarksDashboard' },
      visible: isEarningsBenchmarksEnabled.value,
    },
    {
      title: t('navigationMenu.agents'),
      prependIcon: 'business',
      to: { name: 'Agents' },
      visible: isAgentsEnabled.value,
    },
    {
      title: t('navigationMenu.teamRoster'),
      prependIcon: 'contacts',
      to: { name: 'Roster' },
      visible: isTeamAdmin.value,
    },
    {
      title: t('navigationMenu.settings'),
      prependIcon: 'settings',
      to: { name: 'Settings' },
      visible: isTeamAdmin.value || userStore.user?.isSuperAdmin,
    },
  ]).filter(isMenuItemShown));

  const appConfig = useAppConfig();

  const appendedItems = computed(() => withDefaults([
    {
      title: t('navigationMenu.search'),
      enabled: false,
      visible: false,
    },
    {
      title: t('navigationMenu.getHelp'),
      action: toggleMaven,
    },
    {
      title: t('navigationMenu.helpCenter'),
      link: appConfig.helpUrl,
    },
  ]).filter(isMenuItemShown));

  const userItems = computed(() => withDefaults([
    {
      title: t('logOut'),
      action: async () => {
        await useAuth().logOut();
        redirectUnauthenticatedUser();
      },
    },
  ]).filter(isMenuItemShown));

  return {
    mainItems,
    appendedItems,
    userItems,
  };
}
